import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface Adjustment {
  id: string;
  product: string;
  sku: string;
  type: "increase" | "decrease" | "damage" | "expired" | "count";
  quantity: number;
  reason: string;
  location: string;
  createdBy: string;
  createdAt: string;
  status: "pending" | "approved" | "rejected";
}

const MOCK_ADJUSTMENTS: Adjustment[] = [
  { id: "ADJ-001", product: "Premium Headphones", sku: "ELEC-001", type: "increase", quantity: 25, reason: "Received shipment", location: "Warehouse A", createdBy: "John Doe", createdAt: "2024-01-15 10:30", status: "approved" },
  { id: "ADJ-002", product: "USB-C Cable", sku: "ELEC-003", type: "damage", quantity: -5, reason: "Water damage in storage", location: "Warehouse B", createdBy: "Jane Smith", createdAt: "2024-01-15 09:15", status: "pending" },
  { id: "ADJ-003", product: "Cotton T-Shirt", sku: "CLTH-001", type: "count", quantity: -8, reason: "Physical count discrepancy", location: "Store Front", createdBy: "Mike Johnson", createdAt: "2024-01-14 16:45", status: "approved" },
  { id: "ADJ-004", product: "Running Shoes", sku: "CLTH-003", type: "expired", quantity: -3, reason: "Display items worn", location: "Store Front", createdBy: "Sarah Wilson", createdAt: "2024-01-14 14:20", status: "rejected" },
  { id: "ADJ-005", product: "Wireless Mouse", sku: "ELEC-002", type: "increase", quantity: 50, reason: "Transfer from warehouse", location: "Warehouse A", createdBy: "John Doe", createdAt: "2024-01-13 11:00", status: "approved" },
];

export default function AdjustmentsSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<string>("all");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredAdjustments = MOCK_ADJUSTMENTS.filter((item) => {
    const matchesSearch =
      item.product.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || item.type === filterType;
    return matchesSearch && matchesType;
  });

  const pendingCount = MOCK_ADJUSTMENTS.filter((a) => a.status === "pending").length;
  const todayAdjustments = MOCK_ADJUSTMENTS.filter((a) => a.createdAt.includes("2024-01-15")).length;
  const totalIncrease = MOCK_ADJUSTMENTS.filter((a) => a.quantity > 0).reduce((acc, a) => acc + a.quantity, 0);
  const totalDecrease = Math.abs(MOCK_ADJUSTMENTS.filter((a) => a.quantity < 0).reduce((acc, a) => acc + a.quantity, 0));

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "increase":
        return { icon: "ri-add-circle-line", color: "text-emerald-500" };
      case "decrease":
        return { icon: "ri-indeterminate-circle-line", color: "text-red-500" };
      case "damage":
        return { icon: "ri-error-warning-line", color: "text-amber-500" };
      case "expired":
        return { icon: "ri-time-line", color: "text-orange-500" };
      case "count":
        return { icon: "ri-calculator-line", color: "text-blue-500" };
      default:
        return { icon: "ri-question-line", color: "text-gray-500" };
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return { color: "text-emerald-500 bg-emerald-500/20", label: t("posInventory.approved") };
      case "pending":
        return { color: "text-amber-500 bg-amber-500/20", label: t("posInventory.pending") };
      case "rejected":
        return { color: "text-red-500 bg-red-500/20", label: t("posInventory.rejected") };
      default:
        return { color: "text-gray-500 bg-gray-500/20", label: status };
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posInventory.adjustmentsTitle")}</h1>
          <p className={themeClasses.textSecondary}>{t("posInventory.adjustmentsSubtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}>
            <i className="ri-download-2-line"></i>
            {t("posInventory.export")}
          </button>
          <button className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2">
            <i className="ri-add-line"></i>
            {t("posInventory.newAdjustment")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.pendingApproval")}</span>
            <i className="ri-time-line text-amber-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-amber-500`}>{pendingCount}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.todayAdjustments")}</span>
            <i className="ri-calendar-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{todayAdjustments}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.totalIncrease")}</span>
            <i className="ri-arrow-up-circle-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-emerald-500`}>+{totalIncrease}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.totalDecrease")}</span>
            <i className="ri-arrow-down-circle-line text-red-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-red-500`}>-{totalDecrease}</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("posInventory.searchAdjustments")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <div className="flex gap-2">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            >
              <option value="all">{t("posInventory.allTypes")}</option>
              <option value="increase">{t("posInventory.increase")}</option>
              <option value="decrease">{t("posInventory.decrease")}</option>
              <option value="damage">{t("posInventory.damage")}</option>
              <option value="expired">{t("posInventory.expired")}</option>
              <option value="count">{t("posInventory.count")}</option>
            </select>
            <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} flex items-center gap-2`}>
              <i className="ri-filter-line"></i>
              {t("posInventory.filters")}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.adjustmentId")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.product")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.type")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.quantity")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.reason")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.createdBy")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.status")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredAdjustments.map((item) => {
                const typeInfo = getTypeIcon(item.type);
                const statusBadge = getStatusBadge(item.status);
                return (
                  <tr key={item.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                    <td className={`px-4 py-3 font-mono text-sm ${themeClasses.text}`}>{item.id}</td>
                    <td className="px-4 py-3">
                      <div>
                        <p className={`font-medium ${themeClasses.text}`}>{item.product}</p>
                        <p className={`text-sm ${themeClasses.textSecondary}`}>{item.sku}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <i className={`${typeInfo.icon} text-xl ${typeInfo.color}`}></i>
                    </td>
                    <td className={`px-4 py-3 text-right font-bold ${item.quantity > 0 ? "text-emerald-500" : "text-red-500"}`}>
                      {item.quantity > 0 ? `+${item.quantity}` : item.quantity}
                    </td>
                    <td className={`px-4 py-3 ${themeClasses.textSecondary} max-w-[200px] truncate`}>{item.reason}</td>
                    <td className="px-4 py-3">
                      <div>
                        <p className={`font-medium ${themeClasses.text}`}>{item.createdBy}</p>
                        <p className={`text-sm ${themeClasses.textSecondary}`}>{item.createdAt}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge.color}`}>
                        {statusBadge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button aria-label={t("general.viewDetails", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                          <i className="ri-eye-line"></i>
                        </button>
                        {item.status === "pending" && (
                          <>
                            <button className="p-2 rounded-lg hover:bg-emerald-500/20 text-emerald-500">
                              <i className="ri-check-line"></i>
                            </button>
                            <button aria-label={t("general.close", { ns: "common" })} className="p-2 rounded-lg hover:bg-red-500/20 text-red-500">
                              <i className="ri-close-line"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
